/* eslint-disable max-lines */
import { styled, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getCompanyJobs } from '../../../services/jobs';
import { getUserByID } from '../../../services/user';
import thumbsUp from '../../../static/icons/thumbs-up.svg';
import { UserContext } from '../../context/UserContext';
import { BaseContainer } from '../../global/Core';

import ContactCard from './ContactCard';

import type { SimpleInterview } from '../../../types/interview';
import type { Job } from '../../../types/job';

type Contact = {
    id: number;
    participantID: number;
    userID: string;
    firstName: string;
    lastName: string;
    currentJobTitle: string;
    profilePicturePath: string;
    interviews: {
        id: number;
        jobID: number;
        companyID: number;
    }[];
};

type GroupCandidateByStatus = {
    contacts: Contact[];
    pending: Contact[];
    declined: Contact[];
};

const ContentContainer = styled('div')`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
    padding-top: 4rem;
    gap: 1rem;
`;

export default function CandidateNetwork() {
    const [searchByJobID, setSearchByJobID] = useState(0);
    const [openJobs, setOpenJobs] = useState<Job[]>([]);
    const [jobCandidates, setJobCandidates] = useState<GroupCandidateByStatus>({
        contacts: [],
        pending: [],
        declined: []
    });

    const { jobID } = useParams<{ jobID: string }>();

    const { user, companies } = useContext(UserContext);

    const handleSearchByJobID = (event: SelectChangeEvent) =>
        setSearchByJobID(parseInt(event.target.value, 10));

    useEffect(() => {
        if (companies.length > 0) {
            const jobList = companies.map((company) =>
                getCompanyJobs(company.id)
            );
            Promise.all(jobList).then((jobs) => {
                setOpenJobs(
                    jobs
                        .flat()
                        .filter(
                            (job) =>
                                job.open &&
                                job.interviews.some(
                                    (interview: SimpleInterview) =>
                                        interview.status.startsWith('completed')
                                )
                        )
                        .sort(
                            (a, b) =>
                                b.createdAt.toMillis() - a.createdAt.toMillis()
                        )
                );
            });
        }
    }, [companies]);

    useEffect(() => {
        if (openJobs.length > 0) {
            if (jobID) {
                setSearchByJobID(parseInt(jobID, 10));
            } else {
                setSearchByJobID(openJobs[0].id);
            }
        }
    }, [openJobs, jobID]);

    useEffect(() => {
        const candidatesByStatus = {
            contacts: [],
            pending: [],
            declined: []
        } as GroupCandidateByStatus;

        if (!searchByJobID) return;

        const currentJob = openJobs.find((job) => job.id === searchByJobID);
        currentJob?.interviews
            .filter((interview) => interview.status.startsWith('completed'))
            .forEach((interview) => {
                const { candidateID } = interview;
                let status = 'pending' as keyof GroupCandidateByStatus;
                if (interview.status === 'completed_network_accepted') {
                    status = 'contacts';
                }
                let candidateIndex = candidatesByStatus[status].findIndex(
                    (candidate) => candidate.participantID === candidateID
                );
                if (candidateIndex === -1) {
                    candidateIndex = candidatesByStatus.declined.findIndex(
                        (candidate) => candidate.participantID === candidateID
                    );
                }
                if (candidateIndex > -1) {
                    candidatesByStatus[status][candidateIndex].interviews.push({
                        id: interview.id,
                        jobID: interview.jobID,
                        companyID: currentJob.companyID
                    });
                } else {
                    if (interview.status !== 'completed') {
                        status = 'declined';
                    }
                    const newCandidate = {
                        id: interview.id,
                        createdAt: interview.createdAt,
                        participantID: candidateID,
                        userID: '',
                        firstName: '',
                        lastName: '',
                        currentJobTitle: '',
                        profilePicturePath: '',
                        interviews: [
                            {
                                id: interview.id,
                                jobID: interview.jobID,
                                companyID: currentJob.companyID
                            }
                        ]
                    };
                    candidatesByStatus[status].push(newCandidate);
                }
            });
        setJobCandidates(candidatesByStatus);

        (
            ['pending', 'declined'] as (keyof typeof candidatesByStatus)[]
        ).forEach(async (status) => {
            const candidateUsers = candidatesByStatus[status].map((candidate) =>
                getUserByID(candidate.participantID)
            );
            await Promise.all(candidateUsers).then((users) => {
                candidatesByStatus[status] = candidatesByStatus[status].map(
                    (candidate, idx) => ({
                        ...candidate,
                        userID: users[idx].userID,
                        firstName: users[idx].name.firstName,
                        lastName: users[idx].name.lastName,
                        currentJobTitle: users[idx].name.currentJobTitle,
                        profilePicturePath: users[idx].name.imageURL
                    })
                );
                setJobCandidates((prev) => ({
                    ...prev,
                    [status]: candidatesByStatus[status]
                }));
            });
        });
    }, [openJobs, searchByJobID]);

    return (
        <BaseContainer>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '1rem'
                }}
            >
                <Typography color={'text.secondary'} variant={'h1'}>
                    {'Candidates'}
                </Typography>
                <img src={thumbsUp} style={{ marginLeft: '1.5rem' }} />
            </div>
            <Select
                labelId='job-select-label'
                id='job-select'
                value={searchByJobID.toString()}
                onChange={handleSearchByJobID}
                style={{ width: '100%' }}
                color={'primary'}
                sx={{ mt: 1 }}
                MenuProps={{
                    disableScrollLock: true
                }}
            >
                {openJobs.map((job) => (
                    <MenuItem key={job.id} value={job.id}>
                        {`${job.title} - ID #${job.id}`}
                    </MenuItem>
                ))}
            </Select>
            {(
                [
                    'contacts',
                    'pending',
                    'declined'
                ] as (keyof GroupCandidateByStatus)[]
            ).map((status) => (
                <>
                    <Typography
                        variant={'h2'}
                        color={'secondary'}
                        sx={{ mt: 3 }}
                    >
                        {`${status.charAt(0).toUpperCase()}${status.slice(
                            1,
                            status.length
                        )}`}
                    </Typography>
                    <ContentContainer container spacing={2} sx={{ pt: 1 }}>
                        {jobCandidates[status].map((contact, idx) => (
                            <ContactCard
                                key={idx}
                                contact={contact}
                                interviewIDs={contact.interviews.map(
                                    (interview) => interview.id
                                )}
                                uid={user.id}
                                userID={user.userID}
                                role={user.levylRole}
                                status={status}
                            />
                        ))}
                    </ContentContainer>
                </>
            ))}
        </BaseContainer>
    );
}
